import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { OrganizationContentProps } from "@/features/organizations/profile/types";
import Analytics from "@/features/organizations/profile/components/Analytics";
import MemberTable from "@/features/organizations/profile/components/MemberTable";
import BaseTabs from "@/components_v2/Tabs/BaseTabs";
import Settings from "@/features/organizations/profile/components/Settings";
import UserInviteTable from "@/features/organizations/profile/components/UserInviteTable";
import EvidenceTable from "@/domains/evidence/components/EvidenceTable";
import SurveyViewsTable from "@/domains/survey-views/components/SurveyViewTable";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";
import { useOrganizationPermissions } from "@/features/organizations/hooks/useOrganizationPermissions";
import { OrganizationRoles, Roles } from "@/shared/utils/constants";
import { isFullOrganizationResponse } from "@/redux/organizations/typeGuards";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import OrganizationProfileDetailsDisplay from "@/features/organizations/profile/components/OrganizationProfileDetailsDisplay";
import OrganizationProfileDetailsForm from "@/features/organizations/profile/components/OrganizationProfileDetailsForm";

const OrganizationProfileContainer = ({
  organization,
}: OrganizationContentProps) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const permissions = useOrganizationPermissions(currentUser, organization);
  const [isEdit, setIsEdit] = useState(false);

  const handleEdit = () => {
    setIsEdit(true);
  };

  const tabs = useMemo(() => {
    if (!currentUser || !organization) return [];

    return [
      {
        canView: permissions.canViewOverviewTab,
        key: "overview",
        label: t("organizations.labels.overview"),
        children: <Analytics organization={organization} />,
      },
      {
        canView: permissions.canViewMembersTab,
        key: "members",
        label: t("organizations.labels.members"),
        children: (
          <MemberTable
            columnNames={[
              "fullName",
              "email",
              "role",
              ...(permissions.canEdit ? ["actions"] : []),
            ]}
            filterNames={["search", "roles"]}
            defaultFilters={{
              organization: organization.id,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewInvitesTab,
        key: "invites",
        label: t("organizations.labels.invites"),
        children: (
          <UserInviteTable
            columnNames={["email", "role", "actions"]}
            filterNames={["search", "roles"]}
            defaultFilters={{
              organization: organization.id,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewEvidenceTab,
        key: "evidence",
        label: t("organizations.labels.evidence"),
        children: (
          <EvidenceTable
            columnNames={[
              "title",
              "type",
              "contributors",
              "sharingRights",
              "createdAt",
            ]}
            filterNames={[
              "type",
              "sharingRights",
              "contributors",
              "languages",
              "search",
            ]}
            defaultFilters={{
              organizations: [organization.id],
              editable: true,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewReportsTab,
        key: "report-features",
        label: t("organizations.labels.surveyViews"),
        children: (
          <SurveyViewsTable
            columnNames={[
              "title",
              "publicationDestination",
              "dataFormat",
              "survey",
              "createdAt",
            ]}
            filterNames={[
              "search",
              "survey",
              "dataFormat",
              "distributionFormat",
            ]}
            defaultFilters={{
              publicationDestination:
                currentUser.role !== Roles.ADMIN.value &&
                currentUser.organizationRole === OrganizationRoles.GUEST.value
                  ? SurveyViewPublicationDestination.WEBSITE
                  : [
                      SurveyViewPublicationDestination.WEBSITE,
                      SurveyViewPublicationDestination.IN_APP,
                    ],
              organization: organization.id,
            }}
          />
        ),
      },
      {
        canView: permissions.canViewSettingsTab,
        key: "settings",
        label: t("organizations.labels.settings"),
        children: isFullOrganizationResponse(organization, currentUser) ? (
          <Settings organization={organization} />
        ) : null,
      },
    ];
  }, [permissions, currentUser, organization]);

  return (
    <div className="flex gap-400">
      <OrganizationProfileDetailsDisplay
        organization={organization}
        onEdit={handleEdit}
      />
      <OrganizationProfileDetailsForm
        organization={organization}
        open={isEdit}
        setOpen={setIsEdit}
      />

      <BaseTabs
        defaultActiveKey="overview"
        items={tabs.filter((tab) => tab.canView)}
      />
    </div>
  );
};

export default OrganizationProfileContainer;
